import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { LineChart } from '../components/charts';
import {
  ArrowUpRight, ArrowDownRight, RefreshCw, Users, ShoppingCart,
  Eye, MousePointerClick, CreditCard, Package, Loader2, TrendingUp
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// ─── Constants & Helpers ───────────────────────────────────────────────
const API = 'https://api.glowytics.com';

const fmt = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}M`;
  if (v >= 1_000) return `${(v / 1_000).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}K`;
  return v.toLocaleString('tr-TR', { maximumFractionDigits: 0 });
};
const fmtCur = (n) => `₺${fmt(n)}`;
const fmtPct = (n) => `%${Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`;
const fmtDec = (n) => Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
const toYMD = (d) => { const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, '0'); const dy = String(d.getDate()).padStart(2, '0'); return `${y}-${m}-${dy}`; };

const greeting = () => { const h = new Date().getHours(); return h >= 5 && h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'; };

// ─── Main Component ────────────────────────────────────────────────────
export default function TestingOverview() {
  const { user } = useAuth();
  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;

  const [dateRange, setDateRange] = useState('last7');
  const [loading, setLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // Live
  const [liveGads, setLiveGads] = useState(null);
  const [liveMeta, setLiveMeta] = useState(null);
  const [liveGa4, setLiveGa4] = useState(null);
  const [liveUpdatedAt, setLiveUpdatedAt] = useState(null);
  const [liveEcom, setLiveEcom] = useState([]); // [{name, revenue, orders, items}]

  // Performance
  const [googleAdsRows, setGoogleAdsRows] = useState([]);
  const [metaAdsRows, setMetaAdsRows] = useState([]);
  const [loadingPerf, setLoadingPerf] = useState(false);

  // Traffic
  const [trafficRows, setTrafficRows] = useState([]);

  // Events / Funnel
  const [eventsRows, setEventsRows] = useState([]);

  // Monthly trend
  const [monthlyRows, setMonthlyRows] = useState([]);

  // ─── Fetch helpers ─────────────────────────────────────────────
  const mkHeaders = useCallback(() => ({ 'Content-Type': 'application/json', 'X-Brand-ID': brandId || '' }), [brandId]);
  const get = useCallback(async (url) => {
    try {
      const r = await fetch(url, { credentials: 'include', headers: mkHeaders() });
      if (!r.ok) return null;
      return r.json();
    } catch { return null; }
  }, [mkHeaders]);

  // ─── Fetch: Live ───────────────────────────────────────────────
  const fetchLive = useCallback(async () => {
    if (!brandId) return;
    const h = mkHeaders();
    const [g, m, a] = await Promise.allSettled([
      get(`${API}/api/brands/${brandId}/live/google-ads/today`),
      get(`${API}/api/brands/${brandId}/live/meta-ads/today`),
      get(`${API}/api/brands/${brandId}/live/ga4/today`)
    ]);
    setLiveGads(g.status === 'fulfilled' ? g.value : null);
    setLiveMeta(m.status === 'fulfilled' ? m.value : null);
    setLiveGa4(a.status === 'fulfilled' ? a.value : null);
    setLiveUpdatedAt(new Date());

    // E-commerce platforms
    const conn = await get(`${API}/api/auth/connections`);
    const platforms = Array.isArray(conn) ? conn : [];
    const isActive = (p) => platforms.some(c => String(c.platform || '').toUpperCase() === p && String(c.status || '').toUpperCase() === 'ACTIVE');
    const tz = -new Date().getTimezoneOffset();
    const ecom = [];
    if (isActive('IKAS')) {
      const d = await get(`${API}/api/ikas/live/today?tzOffsetMinutes=${tz}`);
      if (d?.totals) ecom.push({ name: 'IKAS', ...d.totals });
    }
    if (isActive('SHOPIFY')) {
      const d = await get(`${API}/api/shopify/live/today?tzOffsetMinutes=${tz}`);
      if (d?.totals) ecom.push({ name: 'Shopify', ...d.totals });
    }
    if (isActive('WOOCOMMERCE')) {
      const d = await get(`${API}/api/woocommerce/live/today?source=api&tzOffsetMinutes=${tz}`);
      if (d?.totals) ecom.push({ name: 'WooCommerce', ...d.totals });
    }
    setLiveEcom(ecom);
  }, [brandId, get, mkHeaders]);

  // ─── Fetch: Unified performance ────────────────────────────────
  const fetchPerf = useCallback(async () => {
    if (!brandId) return;
    setLoadingPerf(true);
    const [gData, mData] = await Promise.all([
      get(`${API}/api/google-ads/unified-googleads-ga4-performance?dateRange=${dateRange}`),
      get(`${API}/api/google-ads/unified-meta-ga4-performance?dateRange=${dateRange}`)
    ]);
    // API returns array directly
    setGoogleAdsRows(Array.isArray(gData) ? gData : (gData?.data || gData?.dailyData || []));
    setMetaAdsRows(Array.isArray(mData) ? mData : (mData?.data || mData?.dailyData || []));
    setLoadingPerf(false);
  }, [brandId, dateRange, get]);

  // ─── Fetch: Traffic ────────────────────────────────────────────
  const fetchTraffic = useCallback(async () => {
    if (!brandId) return;
    const d = await get(`${API}/api/google-ads/ga-traffic-source?dateRange=${dateRange}`);
    setTrafficRows(d?.detailedData || (Array.isArray(d) ? d : []));
  }, [brandId, dateRange, get]);

  // ─── Fetch: Events ─────────────────────────────────────────────
  const fetchEvents = useCallback(async () => {
    if (!brandId) return;
    const d = await get(`${API}/api/google-ads/ga-events-raw?dateRange=${dateRange}`);
    setEventsRows(Array.isArray(d) ? d : (d?.data || d?.rows || []));
  }, [brandId, dateRange, get]);

  // ─── Fetch: Monthly GA summary ─────────────────────────────────
  const fetchMonthly = useCallback(async () => {
    if (!brandId) return;
    const d = await get(`${API}/api/google-ads/ga-summary-monthly?dateRange=all`);
    setMonthlyRows(Array.isArray(d?.data) ? d.data : (Array.isArray(d) ? d : []));
  }, [brandId, get]);

  // ─── Fetch: Logo ───────────────────────────────────────────────
  useEffect(() => {
    if (!brandId) return;
    get(`${API}/api/brand-info/${brandId}/details`).then(d => { if (d?.logoUrl) setLogoUrl(d.logoUrl); });
  }, [brandId, get]);

  // ─── Initial load ──────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchLive(), fetchMonthly()]);
      setLoading(false);
    })();
  }, [fetchLive, fetchMonthly]);

  // ─── On date range change ─────────────────────────────────────
  useEffect(() => { fetchPerf(); fetchTraffic(); fetchEvents(); }, [fetchPerf, fetchTraffic, fetchEvents]);

  const handleRefresh = async () => { setRefreshing(true); await fetchLive(); setRefreshing(false); };

  // ─── Aggregations ──────────────────────────────────────────────
  const agg = (rows, costKey = 'cost') => {
    if (!rows.length) return null;
    return rows.reduce((a, r) => ({
      cost: a.cost + Number(r[costKey] || r.cost || r.spend || r.adsCost || 0),
      clicks: a.clicks + Number(r.clicks || r.adsClicks || 0),
      impressions: a.impressions + Number(r.impressions || r.adsImpressions || 0),
      conversions: a.conversions + Number(r.conversions || r.adsConversions || 0),
      convValue: a.convValue + Number(r.conversion_value || r.conversionValue || r.adsConversionValue || 0),
      sessions: a.sessions + Number(r.ga4_sessions || r.sessions || r.gaSessions || 0),
      transactions: a.transactions + Number(r.ga4_transactions || r.transactions || r.gaTransactions || 0),
      revenue: a.revenue + Number(r.ga4_total_revenue || r.revenue || r.totalRevenue || r.gaRevenue || 0),
    }), { cost: 0, clicks: 0, impressions: 0, conversions: 0, convValue: 0, sessions: 0, transactions: 0, revenue: 0 });
  };

  const gAgg = useMemo(() => agg(googleAdsRows, 'cost'), [googleAdsRows]);
  const mAgg = useMemo(() => agg(metaAdsRows, 'spend'), [metaAdsRows]);

  const totalKpi = useMemo(() => {
    const g = gAgg || {}, m = mAgg || {};
    const spend = (g.cost || 0) + (m.cost || 0);
    const rev = (g.revenue || 0) + (m.revenue || 0);
    const ses = (g.sessions || 0) + (m.sessions || 0);
    const tx = (g.transactions || 0) + (m.transactions || 0);
    return { spend, rev, ses, tx, roas: spend > 0 ? rev / spend : 0, cr: ses > 0 ? (tx / ses) * 100 : 0 };
  }, [gAgg, mAgg]);

  // Traffic top sources
  const topSources = useMemo(() => {
    const map = {};
    for (const r of trafficRows) {
      const k = r.sessionSourceMedium || r.source_medium || `${r.source || 'direct'}/${r.medium || 'none'}`;
      if (!map[k]) map[k] = { source: k, sessions: 0, revenue: 0 };
      map[k].sessions += Number(r.sessions || 0);
      map[k].revenue += Number(r.totalRevenue || r.revenue || 0);
    }
    return Object.values(map).sort((a, b) => b.sessions - a.sessions).slice(0, 6);
  }, [trafficRows]);

  // Funnel
  const funnel = useMemo(() => {
    const em = {};
    for (const r of eventsRows) {
      const n = r.eventName || r.event_name || '';
      em[n] = (em[n] || 0) + Number(r.eventCount || r.event_count || 0);
    }
    const steps = [
      { label: 'Product Views', key: 'view_item', icon: Eye },
      { label: 'Add to Cart', key: 'add_to_cart', icon: ShoppingCart },
      { label: 'View Cart', key: 'view_cart', icon: MousePointerClick },
      { label: 'Checkout', key: 'begin_checkout', icon: CreditCard },
      { label: 'Purchase', key: 'purchase', icon: Package },
    ].map(s => ({ ...s, value: em[s.key] || 0 }));
    const mx = Math.max(...steps.map(s => s.value), 1);
    return steps.map((s, i) => ({
      ...s,
      pct: (s.value / mx) * 100,
      drop: i > 0 && steps[i - 1].value > 0 ? ((steps[i - 1].value - s.value) / steps[i - 1].value) * 100 : 0
    }));
  }, [eventsRows]);

  // Chart data
  const chartData = useMemo(() => {
    if (!monthlyRows.length) return null;
    const sorted = [...monthlyRows].sort((a, b) => (a.month || a.date || '').localeCompare(b.month || b.date || '')).slice(-12);
    return {
      labels: sorted.map(r => { const d = new Date((r.month || r.date) + '-01'); return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }); }),
      datasets: [
        {
          label: 'Revenue',
          data: sorted.map(r => Number(r.totalRevenue || r.revenue || r.total_revenue || 0)),
          borderColor: '#6366f1',
          backgroundColor: (ctx) => {
            const g = ctx.chart.ctx.createLinearGradient(0, 0, 0, 280);
            g.addColorStop(0, 'rgba(99,102,241,0.15)');
            g.addColorStop(1, 'rgba(99,102,241,0)');
            return g;
          },
          fill: true, tension: 0.4, pointRadius: 0, pointHoverRadius: 5, borderWidth: 2.5,
        },
        {
          label: 'Sessions',
          data: sorted.map(r => Number(r.sessions || r.totalSessions || r.total_sessions || 0)),
          borderColor: '#06b6d4',
          backgroundColor: 'transparent',
          borderDash: [4, 4], tension: 0.4, pointRadius: 0, pointHoverRadius: 4, borderWidth: 1.5, yAxisID: 'y1',
        },
      ],
    };
  }, [monthlyRows]);

  const chartOpts = {
    responsive: true, maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(15,23,42,0.9)', cornerRadius: 10, padding: 14,
        titleFont: { size: 12, weight: '600' }, bodyFont: { size: 11 },
        callbacks: { label: (c) => ` ${c.dataset.label}: ${c.dataset.label === 'Revenue' ? fmtCur(c.raw) : fmt(c.raw)}` }
      }
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10 }, color: '#94a3b8' } },
      y: { grid: { color: 'rgba(0,0,0,0.03)', drawBorder: false }, ticks: { font: { size: 10 }, color: '#94a3b8', callback: (v) => v >= 1e6 ? `${(v / 1e6).toFixed(0)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(0)}K` : v } },
      y1: { position: 'right', grid: { display: false }, ticks: { font: { size: 10 }, color: '#94a3b8', callback: (v) => v >= 1e3 ? `${(v / 1e3).toFixed(0)}K` : v } }
    }
  };

  // ─── Loading ───────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-200 animate-pulse">
              <TrendingUp className="w-7 h-7 text-white" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: '0ms' }} />
            <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: '150ms' }} />
            <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
        </div>
      </div>
    );
  }

  // ─── Helpers (render) ──────────────────────────────────────────
  const dateFilters = [
    { v: 'yesterday', l: 'Yesterday' }, { v: 'last7', l: '7D' }, { v: 'last30', l: '30D' },
    { v: 'thisMonth', l: 'MTD' }, { v: 'lastMonth', l: 'Last Mo' }, { v: 'last90', l: '90D' },
  ];

  const ChangeBadge = ({ val }) => {
    if (val == null) return null;
    const pos = Number(val) >= 0;
    return (
      <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${pos ? 'text-emerald-700 bg-emerald-50' : 'text-rose-700 bg-rose-50'}`}>
        {pos ? <ArrowUpRight className="w-2.5 h-2.5" /> : <ArrowDownRight className="w-2.5 h-2.5" />}
        {Math.abs(Number(val)).toFixed(1)}%
      </span>
    );
  };

  // ─── Render ────────────────────────────────────────────────────
  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f8fafc 100%)' }}>
      <div className="px-5 py-6 max-w-[1400px] mx-auto">

        {/* ── Header ──────────────────────────── */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            {logoUrl && <img src={logoUrl} alt="" className="w-9 h-9 rounded-xl object-cover ring-2 ring-white shadow" />}
            <div>
              <h1 className="text-lg font-bold text-slate-900">{greeting()}, {user?.name?.split(' ')[0] || 'there'}</h1>
              <p className="text-[11px] text-slate-400 mt-0.5">Performance overview</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center bg-white/80 backdrop-blur rounded-xl border border-slate-200/60 shadow-sm p-0.5">
              {dateFilters.map(f => (
                <button key={f.v} onClick={() => setDateRange(f.v)}
                  className={`px-2.5 py-1.5 text-[11px] font-medium rounded-[10px] transition-all ${dateRange === f.v ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                  {f.l}
                </button>
              ))}
            </div>
            <button onClick={handleRefresh} disabled={refreshing}
              className="w-8 h-8 rounded-xl bg-white/80 backdrop-blur border border-slate-200/60 shadow-sm flex items-center justify-center hover:bg-white transition-all disabled:opacity-50">
              <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* ── KPI Strip ───────────────────────── */}
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {[
            { label: 'Revenue', value: fmtCur(totalKpi.rev), gradient: 'from-emerald-500 to-teal-600' },
            { label: 'Ad Spend', value: fmtCur(totalKpi.spend), gradient: 'from-rose-500 to-pink-600' },
            { label: 'ROAS', value: `${fmtDec(totalKpi.roas)}x`, gradient: 'from-amber-500 to-orange-600' },
            { label: 'Sessions', value: fmt(totalKpi.ses), gradient: 'from-blue-500 to-indigo-600' },
            { label: 'Transactions', value: fmt(totalKpi.tx), gradient: 'from-violet-500 to-purple-600' },
            { label: 'Conv. Rate', value: fmtPct(totalKpi.cr), gradient: 'from-cyan-500 to-sky-600' },
          ].map((kpi, i) => (
            <div key={i} className="relative overflow-hidden bg-white/70 backdrop-blur-sm rounded-2xl border border-white/80 shadow-sm hover:shadow-md transition-all group">
              <div className={`absolute top-0 right-0 w-16 h-16 bg-gradient-to-br ${kpi.gradient} opacity-[0.07] rounded-bl-[40px] group-hover:opacity-[0.12] transition-opacity`} />
              <div className="p-4">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">{kpi.label}</p>
                <p className="mt-1.5 text-xl font-bold text-slate-900">{kpi.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Live Today ──────────────────────── */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <h2 className="text-sm font-semibold text-slate-800">Live Today</h2>
            </div>
            {liveUpdatedAt && <span className="text-[10px] text-slate-400">{liveUpdatedAt.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Google Ads */}
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/80 shadow-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/c/c7/Google_Ads_logo.svg" alt="" className="w-4 h-4" />
                </div>
                <span className="text-xs font-semibold text-slate-700">Google Ads</span>
                <span className="ml-auto text-[9px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-md">LIVE</span>
              </div>
              <div className="grid grid-cols-3 gap-y-3 gap-x-2">
                {[
                  { l: 'Cost', v: fmtCur(liveGads?.cost) }, { l: 'Clicks', v: fmt(liveGads?.clicks) }, { l: 'Conv.', v: fmt(liveGads?.conversions) },
                  { l: 'Conv. Value', v: fmtCur(liveGads?.conversion_value) }, { l: 'CTR', v: fmtPct(liveGads?.ctr) }, { l: 'CPC', v: fmtCur(liveGads?.cpc) },
                ].map((m, i) => (
                  <div key={i}><p className="text-[9px] text-slate-400 uppercase font-medium">{m.l}</p><p className="text-sm font-bold text-slate-800 mt-0.5">{m.v}</p></div>
                ))}
              </div>
            </div>
            {/* Meta Ads */}
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/80 shadow-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-indigo-50 flex items-center justify-center">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/0/05/Facebook_Logo_%282019%29.png" alt="" className="w-4 h-4" />
                </div>
                <span className="text-xs font-semibold text-slate-700">Meta Ads</span>
                <span className="ml-auto text-[9px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-md">LIVE</span>
              </div>
              <div className="grid grid-cols-3 gap-y-3 gap-x-2">
                {[
                  { l: 'Spend', v: fmtCur(liveMeta?.spend) }, { l: 'Clicks', v: fmt(liveMeta?.clicks) }, { l: 'Conv.', v: fmt(liveMeta?.conversions) },
                  { l: 'Conv. Value', v: fmtCur(liveMeta?.conversion_value) }, { l: 'CTR', v: fmtPct(liveMeta?.ctr) }, { l: 'CPC', v: fmtCur(liveMeta?.cpc) },
                ].map((m, i) => (
                  <div key={i}><p className="text-[9px] text-slate-400 uppercase font-medium">{m.l}</p><p className="text-sm font-bold text-slate-800 mt-0.5">{m.v}</p></div>
                ))}
              </div>
            </div>
            {/* GA4 */}
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/80 shadow-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none"><path d="M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10z" fill="#F9AB00"/><path d="M12 2a10 10 0 0 1 0 20V12H2a10 10 0 0 1 10-10z" fill="#E37400"/></svg>
                </div>
                <span className="text-xs font-semibold text-slate-700">Google Analytics</span>
                <span className="ml-auto text-[9px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-md">LIVE</span>
              </div>
              <div className="grid grid-cols-3 gap-y-3 gap-x-2">
                {[
                  { l: 'Sessions', v: fmt(liveGa4?.sessions) }, { l: 'Users', v: fmt(liveGa4?.users) }, { l: 'Transactions', v: fmt(liveGa4?.transactions) },
                  { l: 'Revenue', v: fmtCur(liveGa4?.revenue) }, { l: 'Bounce', v: fmtPct(liveGa4?.bounceRate) }, { l: 'Avg Dur.', v: `${Math.round(Number(liveGa4?.avgSessionDuration || 0))}s` },
                ].map((m, i) => (
                  <div key={i}><p className="text-[9px] text-slate-400 uppercase font-medium">{m.l}</p><p className="text-sm font-bold text-slate-800 mt-0.5">{m.v}</p></div>
                ))}
              </div>
            </div>
          </div>
          {/* E-commerce platforms */}
          {liveEcom.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
              {liveEcom.map((ec, i) => (
                <div key={i} className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/80 shadow-sm p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 rounded-lg bg-purple-50 flex items-center justify-center">
                      <ShoppingCart className="w-3.5 h-3.5 text-purple-500" />
                    </div>
                    <span className="text-xs font-semibold text-slate-700">{ec.name}</span>
                    <span className="ml-auto text-[9px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-md">LIVE</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div><p className="text-[9px] text-slate-400 uppercase font-medium">Revenue</p><p className="text-sm font-bold text-slate-800 mt-0.5">{fmtCur(ec.revenue)}</p></div>
                    <div><p className="text-[9px] text-slate-400 uppercase font-medium">Orders</p><p className="text-sm font-bold text-slate-800 mt-0.5">{fmt(ec.orders)}</p></div>
                    <div><p className="text-[9px] text-slate-400 uppercase font-medium">Items</p><p className="text-sm font-bold text-slate-800 mt-0.5">{fmt(ec.items)}</p></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Chart + Channel Performance ──────── */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-6">
          {/* Trend Chart */}
          <div className="lg:col-span-3 bg-white/70 backdrop-blur-sm rounded-2xl border border-white/80 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-slate-800">Monthly Trend</h2>
              <div className="flex items-center gap-4 text-[10px]">
                <div className="flex items-center gap-1.5"><div className="w-3 h-0.5 rounded bg-indigo-500" /><span className="text-slate-500">Revenue</span></div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-0.5 rounded bg-cyan-500 border-dashed" style={{ borderBottom: '1.5px dashed #06b6d4', height: 0 }} /><span className="text-slate-500">Sessions</span></div>
              </div>
            </div>
            {chartData ? (
              <LineChart data={chartData} options={chartOpts} height={260} />
            ) : (
              <div className="flex items-center justify-center h-[260px] text-sm text-slate-300">No trend data</div>
            )}
          </div>

          {/* Channel Cards */}
          <div className="lg:col-span-2 flex flex-col gap-3">
            {/* Google Ads */}
            <div className="flex-1 bg-gradient-to-br from-blue-50/80 to-indigo-50/50 backdrop-blur-sm rounded-2xl border border-blue-100/60 shadow-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 rounded-full bg-blue-500" />
                <span className="text-xs font-semibold text-slate-700">Google Ads + GA4</span>
                {loadingPerf && <Loader2 className="w-3 h-3 animate-spin text-slate-400 ml-auto" />}
              </div>
              {gAgg ? (
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  {[
                    { l: 'Spend', v: fmtCur(gAgg.cost) }, { l: 'Revenue', v: fmtCur(gAgg.revenue) },
                    { l: 'ROAS', v: gAgg.cost > 0 ? `${fmtDec(gAgg.revenue / gAgg.cost)}x` : '-' }, { l: 'Sessions', v: fmt(gAgg.sessions) },
                    { l: 'Transactions', v: fmt(gAgg.transactions) }, { l: 'Conv. Rate', v: gAgg.sessions > 0 ? fmtPct((gAgg.transactions / gAgg.sessions) * 100) : '-' },
                  ].map((m, i) => (
                    <div key={i} className="py-1"><p className="text-[9px] text-slate-400 uppercase font-medium">{m.l}</p><p className="text-base font-bold text-slate-900 mt-0.5">{m.v}</p></div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400 py-4 text-center">{loadingPerf ? 'Loading…' : 'No data'}</p>
              )}
            </div>
            {/* Meta Ads */}
            <div className="flex-1 bg-gradient-to-br from-orange-50/80 to-amber-50/50 backdrop-blur-sm rounded-2xl border border-orange-100/60 shadow-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 rounded-full bg-orange-500" />
                <span className="text-xs font-semibold text-slate-700">Meta Ads + GA4</span>
                {loadingPerf && <Loader2 className="w-3 h-3 animate-spin text-slate-400 ml-auto" />}
              </div>
              {mAgg ? (
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  {[
                    { l: 'Spend', v: fmtCur(mAgg.cost) }, { l: 'Revenue', v: fmtCur(mAgg.revenue) },
                    { l: 'ROAS', v: mAgg.cost > 0 ? `${fmtDec(mAgg.revenue / mAgg.cost)}x` : '-' }, { l: 'Sessions', v: fmt(mAgg.sessions) },
                    { l: 'Transactions', v: fmt(mAgg.transactions) }, { l: 'Conv. Rate', v: mAgg.sessions > 0 ? fmtPct((mAgg.transactions / mAgg.sessions) * 100) : '-' },
                  ].map((m, i) => (
                    <div key={i} className="py-1"><p className="text-[9px] text-slate-400 uppercase font-medium">{m.l}</p><p className="text-base font-bold text-slate-900 mt-0.5">{m.v}</p></div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400 py-4 text-center">{loadingPerf ? 'Loading…' : 'No data'}</p>
              )}
            </div>
          </div>
        </div>

        {/* ── Traffic + Funnel ─────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Traffic */}
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/80 shadow-sm p-5">
            <h2 className="text-sm font-semibold text-slate-800 mb-4">Top Traffic Sources</h2>
            {topSources.length ? (
              <div className="space-y-3">
                {topSources.map((s, i) => {
                  const mx = topSources[0]?.sessions || 1;
                  return (
                    <div key={i}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-medium text-slate-700 truncate max-w-[180px]">{s.source}</span>
                        <div className="flex items-center gap-3 text-[10px] text-slate-400 flex-shrink-0">
                          <span>{fmt(s.sessions)}</span>
                          <span className="font-medium text-slate-600">{fmtCur(s.revenue)}</span>
                        </div>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-gradient-to-r from-indigo-400 to-indigo-500 transition-all duration-700" style={{ width: `${(s.sessions / mx) * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-slate-300 text-center py-10">No data</p>
            )}
          </div>

          {/* Funnel */}
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/80 shadow-sm p-5">
            <h2 className="text-sm font-semibold text-slate-800 mb-4">Conversion Funnel</h2>
            {funnel.some(s => s.value > 0) ? (
              <div className="space-y-2.5">
                {funnel.map((s, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <div className="w-24 flex-shrink-0 text-right flex items-center justify-end gap-1.5">
                      <s.icon className="w-3 h-3 text-slate-400" />
                      <span className="text-[10px] text-slate-500">{s.label}</span>
                    </div>
                    <div className="flex-1 h-7 bg-slate-50 rounded-lg overflow-hidden relative">
                      <div className="h-full rounded-lg transition-all duration-700"
                        style={{ width: `${Math.max(6, s.pct)}%`, background: `linear-gradient(90deg, #6366f1 0%, #818cf8 100%)` }} />
                      <div className="absolute inset-0 flex items-center px-2.5">
                        <span className="text-[10px] font-bold text-slate-800">{fmt(s.value)}</span>
                        <span className="text-[9px] text-slate-400 ml-1">{fmtPct(s.pct)}</span>
                      </div>
                    </div>
                    <div className="w-12 flex-shrink-0 text-right">
                      {i > 0 && s.drop > 0 && <span className="text-[9px] text-rose-400 font-semibold">-{fmtPct(s.drop)}</span>}
                    </div>
                  </div>
                ))}
                {/* Summary */}
                {funnel[0].value > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-3 gap-3">
                    <div><p className="text-[9px] text-slate-400 uppercase font-medium">Conv. Rate</p><p className="text-base font-bold text-slate-900">{fmtPct(funnel[0].value > 0 ? (funnel[4].value / funnel[0].value) * 100 : 0)}</p></div>
                    <div><p className="text-[9px] text-slate-400 uppercase font-medium">Cart Rate</p><p className="text-base font-bold text-slate-900">{fmtPct(funnel[0].value > 0 ? (funnel[1].value / funnel[0].value) * 100 : 0)}</p></div>
                    <div><p className="text-[9px] text-slate-400 uppercase font-medium">Checkout→Buy</p><p className="text-base font-bold text-slate-900">{fmtPct(funnel[3].value > 0 ? (funnel[4].value / funnel[3].value) * 100 : 0)}</p></div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-slate-300 text-center py-10">No funnel data</p>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
